import { faSnowplow } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { motion } from 'framer-motion'
import {
  ChevronRight,
  Layers,
  LogOut,
  Moon,
  Settings,
  Snowflake,
  Sun,
  Upload,
  User,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { FaLayerGroup } from 'react-icons/fa'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../Contexts/AuthContext'
import { useMobileMenu } from '../../Contexts/MobileMenuContext'
import { useModal } from '../../Contexts/ModalContext'
import { toast } from '../../lib/toast'
import { useTheme } from '../../theme/hooks'
import ConfirmModal from '../shared/ConfirmModal'

type MenuItem = {
  key: string
  label: string
  icon?: React.ReactNode
  image?: string
  action: () => void
  visible?: boolean
}

const MobileMoreSheet = () => {
  const { theme, isDark, toggleTheme } = useTheme()
  const { isAuthenticated, hasPermission, hasModule, logout, user } = useAuth()
  const { openModal, closeModal } = useModal()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    isMoreSheetOpen,
    closeMoreSheet,
    requestLayerPanel,
    requestSnowPanel,
    requestGisPanel,
  } = useMobileMenu()

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)

  // Auto-close sheet on route change
  useEffect(() => {
    closeMoreSheet()
  }, [location.pathname, closeMoreSheet])

  const TARGET_RESORT_ID = 2
  const SERRE_CHE_TOOL_URL = 'http://10.19.32.32:84/'

  const navigateAndClose = (path: string) => {
    closeMoreSheet()
    navigate(path)
  }

  const handleLayerPanel = () => {
    closeMoreSheet()
    if (location.pathname !== '/') {
      navigate('/')
    }
    requestLayerPanel()
  }

  const handleSnowPanel = () => {
    closeMoreSheet()
    if (location.pathname !== '/') {
      navigate('/')
    }
    requestSnowPanel()
  }

  const handleGisPanel = () => {
    closeMoreSheet()
    if (location.pathname !== '/') {
      navigate('/')
    }
    requestGisPanel()
  }

  const handleLogoutClick = () => {
    closeMoreSheet()
    setIsLogoutModalOpen(true)
  }

  const handleConfirmLogout = () => {
    setIsLogoutModalOpen(false)
    logout()
  }

  const handleLidarModal = () => {
    closeMoreSheet()
    if (location.pathname !== '/') {
      navigate('/')
    }
    openModal('lidarModal', {
      mode: 'mobile',
      onUploadSuccess: () => {
        toast.success('Données LiDAR importées avec succès')
        window.dispatchEvent(new CustomEvent('lidar-upload-success'))
        closeModal('lidarModal')
      },
      onClose: () => closeModal('lidarModal'),
    })
  }

  const mapTools: MenuItem[] = [
    {
      key: 'layers',
      label: 'Couches LiDAR',
      icon: <Layers className="h-5 w-5" />,
      action: handleLayerPanel,
      visible: hasModule('snow_depth_lidar'),
    },
    {
      key: 'gis',
      label: 'Couches de données géospatiales',
      icon: <FaLayerGroup className="h-5 w-5" />,
      action: handleGisPanel,
      visible: hasPermission('gis_operational:view'),
    },
    {
      key: 'snow',
      label: 'Mesures neige',
      icon: <Snowflake className="h-5 w-5" />,
      action: handleSnowPanel,
      visible: hasModule('damage'),
    },
    {
      key: 'lidar',
      label: 'Données LiDAR',
      icon: <Upload className="h-5 w-5" />,
      action: handleLidarModal,
      visible: hasPermission('lidar:upload'),
    },
  ].filter(item => item.visible !== false)

  const navigationItems: MenuItem[] = [
    {
      key: 'damage',
      label: 'Damage',
      icon: <FontAwesomeIcon icon={faSnowplow} className="h-5 w-5" />,
      action: () => navigateAndClose('/damage'),
      visible: hasPermission('plans:read'),
    },
    {
      key: 'chart',
      label: 'Chioné',
      image: `/${isDark ? 'chionédark' : 'chioné2light'}.svg`,
      action: () => navigateAndClose('/chart'),
      visible: hasPermission('data:view_meteo'),
    },
    {
      key: 'serre-chevalier',
      label: 'Outil Neige',
      image: '/Chionome_blanc.svg',
      action: () => {
        closeMoreSheet()
        window.open(SERRE_CHE_TOOL_URL, '_blank')
      },
      visible: user?.resort_id === TARGET_RESORT_ID,
    },
    {
      key: 'settings',
      label: 'Paramètres',
      icon: <User className="h-5 w-5" />,
      action: () => navigateAndClose('/settings/profile'),
      visible: isAuthenticated,
    },
    {
      key: 'admin',
      label: 'Administration',
      icon: <Settings className="h-5 w-5" />,
      action: () => navigateAndClose('/admin'),
      visible: hasPermission('admin:access'),
    },
  ].filter(item => item.visible !== false)

  const renderMenuItem = (item: MenuItem) => (
    <button
      key={item.key}
      onClick={item.action}
      className="flex w-full items-center gap-3 rounded-xl px-4 py-3 transition-colors active:scale-[0.98]"
      style={{ color: theme.text.primary }}
    >
      <div
        className="flex h-10 w-10 items-center justify-center rounded-lg"
        style={{ backgroundColor: theme.surface.secondary }}
      >
        {item.image ? (
          <img
            src={item.image}
            alt={item.label}
            className="h-6 w-6 object-contain"
          />
        ) : (
          <span style={{ color: theme.text.secondary }}>{item.icon}</span>
        )}
      </div>
      <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
      <ChevronRight
        className="h-4 w-4"
        style={{ color: theme.text.disabled }}
      />
    </button>
  )

  return (
    <>
      {/* Backdrop - toujours rendu, anime selon l'etat */}
      <motion.div
        initial={false}
        animate={{ opacity: isMoreSheetOpen ? 0.5 : 0 }}
        transition={{ duration: 0.2 }}
        className={`fixed inset-0 z-[60] bg-black md:hidden ${isMoreSheetOpen ? '' : 'pointer-events-none invisible'}`}
        onClick={closeMoreSheet}
      />

      {/* Sheet - toujours rendu, anime selon l'etat */}
      <motion.div
        initial={false}
        animate={{ y: isMoreSheetOpen ? 0 : '100%' }}
        transition={{ type: 'spring', stiffness: 400, damping: 35 }}
        className={`fixed bottom-0 left-0 right-0 z-[60] max-h-[80vh] overflow-y-auto rounded-t-2xl md:hidden ${isMoreSheetOpen ? '' : 'pointer-events-none invisible'}`}
        style={{ backgroundColor: theme.surface.primary }}
      >
        {/* Handle */}
        <div className="flex justify-center pb-2 pt-3">
          <div
            className="h-1 w-10 rounded-full"
            style={{ backgroundColor: theme.border.primary }}
          />
        </div>

        {/* Map tools section */}
        <div className="px-4 pb-2">
          <p
            className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wider"
            style={{ color: theme.text.secondary }}
          >
            Outils carte
          </p>
          {mapTools.map(renderMenuItem)}
        </div>

        {/* Separator */}
        <div
          className="mx-4 h-px"
          style={{ backgroundColor: theme.border.primary }}
        />

        {/* Navigation section */}
        <div className="px-4 py-2">
          <p
            className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wider"
            style={{ color: theme.text.secondary }}
          >
            Navigation
          </p>
          {navigationItems.map(renderMenuItem)}
        </div>

        {/* Separator */}
        <div
          className="mx-4 h-px"
          style={{ backgroundColor: theme.border.primary }}
        />

        {/* Theme toggle */}
        <div className="px-4 py-2">
          <button
            onClick={toggleTheme}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 transition-colors"
            style={{ color: theme.text.primary }}
          >
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg"
              style={{ backgroundColor: theme.surface.secondary }}
            >
              {isDark ? (
                <Sun
                  className="h-5 w-5"
                  style={{ color: theme.text.secondary }}
                />
              ) : (
                <Moon
                  className="h-5 w-5"
                  style={{ color: theme.text.secondary }}
                />
              )}
            </div>
            <span className="flex-1 text-left text-sm font-medium">
              {isDark ? 'Mode clair' : 'Mode sombre'}
            </span>
            <div
              className="flex h-6 w-11 items-center rounded-full px-0.5 transition-colors"
              style={{
                backgroundColor: isDark
                  ? theme.text.link
                  : theme.surface.secondary,
              }}
            >
              <motion.div
                className="h-5 w-5 rounded-full bg-white shadow-sm"
                animate={{ x: isDark ? 20 : 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30,
                }}
              />
            </div>
          </button>
        </div>

        {/* Separator */}
        <div
          className="mx-4 h-px"
          style={{ backgroundColor: theme.border.primary }}
        />

        {/* Logout */}
        {isAuthenticated && (
          <div className="px-4 pb-6 pt-2">
            <button
              onClick={handleLogoutClick}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 transition-colors"
            >
              <div
                className="flex h-10 w-10 items-center justify-center rounded-lg"
                style={{
                  backgroundColor: isDark
                    ? 'rgba(239, 68, 68, 0.15)'
                    : 'rgba(239, 68, 68, 0.1)',
                }}
              >
                <LogOut className="h-5 w-5 text-red-500" />
              </div>
              <span className="flex-1 text-left text-sm font-medium text-red-500">
                Déconnexion
              </span>
            </button>
          </div>
        )}
      </motion.div>

      <ConfirmModal
        type="warning"
        isOpen={isLogoutModalOpen}
        title="Déconnexion"
        message="Voulez-vous vraiment vous déconnecter ?"
        onConfirm={handleConfirmLogout}
        onCancel={() => setIsLogoutModalOpen(false)}
        confirmText="Se déconnecter"
        cancelText="Annuler"
      />
    </>
  )
}

export default MobileMoreSheet
